import {Component, EventEmitter, Injector, Input, Output, ViewChild} from '@angular/core';
import {ModalComponent} from '@components/modal/modal.component';
import {EquipmentCardEnums, EquipmentCardInterfaces, EquipmentEnums, EquipmentInterfaces} from '@namespaces/equipment.namespace';
import {EquipmentDataService} from '../equipment-data.service';
import {EquipmentSearchService} from './equipment-search.service';
import {FrontendFormComponent} from '@abstract/frontend.form.component';
import {EquipmentCreateOptionsService} from '@services/equipment-create-options.service';
import {AuthService} from '@services/auth.service';
import {UserNamespace} from '@namespaces/user.namespace';
import {CardApiService} from '@modules/equipment-card/services/card.api.service';

interface EquipmentModal {
    search(): void;
    edit(card: EquipmentCardInterfaces.Card.ItemView): void;
}

@Component({
    selector: 'app-new-equipment-modal',
    providers: [
        {provide: 'EquipmentSearchService', useClass: EquipmentSearchService},
        EquipmentCreateOptionsService,
    ],
    templateUrl: './equipment-modal.component.html'
})

export class NewEquipmentModalComponent extends FrontendFormComponent implements EquipmentModal {
    @ViewChild(ModalComponent) modal: ModalComponent;

    @Input() methodId: number;

    @Output() saved = new EventEmitter<EquipmentCardInterfaces.Card.ItemView>();

    selectedApparatus: EquipmentInterfaces.Model;

    types = EquipmentEnums.Type;

    public canEditCalibrations = false;

    constructor(
        public dataService: EquipmentDataService,
        public formService: CardApiService,
        private equipmentCreateOptions: EquipmentCreateOptionsService,
        private authService: AuthService,
        injector: Injector,
    ) {
        super(injector);

        this.createEmptyForm();
    }

    init() {
        this.canEditCalibrations = this.authService.hasPermissions([
            UserNamespace.Permission.equipmentCardCalibrationAdd,
            UserNamespace.Permission.equipmentCardCalibrationUpdate,
        ]);
    }

    search() {
        this.createEmptyForm();
        this.selectedApparatus = null;
        this.showModal();
    }

    edit(card: EquipmentCardInterfaces.Card.ItemView) {
        this.createEmptyForm();
        this.selectedApparatus = null;

        if (card) {
            this.selectedApparatus = card.apparatus;
            this.setFormValues(card, this.form);
        }
        this.showModal();
    }

    onEquipmentSelect(equipment: EquipmentInterfaces.Model) {
        this.selectedApparatus = equipment;

        if (!equipment) {
            this.createEmptyForm();
        }
    }

    /**
     * Предотвращаем отправку формы, пока не выбрана модель.
     * Нужно в для предоствращения отправки через ctrl + Enter и подтверждения для списания
     * @returns {boolean}
     */
    preventSave() {
        /**
         * Если оборудование не выбрано и мы умудрились как-то сохранить форму, то не даем этого сделать
         */
        if (!this.selectedApparatus) {
            return true;
        }

        const statusControl = this.form.get('status');

        if (!statusControl) {
            return false;
        }

        const isDismissedStatus = +statusControl.value === EquipmentCardEnums.EquipmentStatus.dismiss;

        if (isDismissedStatus) {
            /**
             * Нужно подтверждение сохранения оборудования в статусе «Списано»
             * Не используем template strings для всего сообщения, так как тогда возникают проблемы с выравниванием в confirm
             */
            const message = 'Списанное оборудование не учитывается в аттестации лаборатории, но всегда будет доступно на складе.\n\n' +
            `Вы уверены, что хотите списать «${this.selectedApparatus.title}»?`;
            return !confirm(message);
        } else {
            return false;
        }
    }

    afterSave(data: EquipmentCardInterfaces.Card.ItemView) {
        this.isPending = false;
        this.saved.emit(data);
        this.modal.close();
    }

    private showModal() {
        this.equipmentCreateOptions.setDefaultGroup(this.dataService.group$.getValue());
        this.equipmentCreateOptions.setAllObjectsByDefault(true);
        this.equipmentCreateOptions.setDefaultMethod(this.dataService.methodId$.getValue());

        if (this.dataService.isReference$.getValue()) {
            this.equipmentCreateOptions.setAvailableTypes([EquipmentEnums.Type.reference]);
        } else {
            this.equipmentCreateOptions.excludeAvailableTypes([EquipmentEnums.Type.reference, EquipmentEnums.Type.device]);
        }

        this.modal.open();
    }

    private createEmptyForm() {
        this.form = this._fb.group({
            id: [''],
            apparatus_id: [''],
            responsible_employee_id: [''],
            calibrations: this._fb.array([]),
            equipments: this._fb.array([]),
            repairs: this._fb.array([]),
            places: this._fb.array([])
        });
    }
}

